import { useEffect, useRef, useState } from 'react';

import { useAuthStore, useHasHydrated } from '@/stores/authStore';
import { useAppModeStore } from '@/stores/appModeStore';
import { fetchAgents } from '@/lib/api';

/**
 * Returns the appropriate home path.
 *
 * In app mode: always '/chat'.
 * In regular mode: '/agents' if the user has any agents, '/build' otherwise.
 *
 * `ready` is false until the auth store has hydrated and the check
 * completes -- callers that redirect should wait for ready.
 */
export function useHomePath(): { path: string; ready: boolean } {
  const isAppMode = useAppModeStore((s) => s.isAppMode);
  const aurigaUid = useAuthStore((s) => s.aurigaUid);
  const sessionToken = useAuthStore((s) => s.sessionToken);
  const hydrated = useHasHydrated();
  const [path, setPath] = useState(isAppMode ? '/chat' : '/build');
  const [ready, setReady] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current || !hydrated) return;

    // App mode: always /chat, no agents fetch needed
    if (isAppMode) {
      setPath('/chat');
      setReady(true);
      return;
    }

    if (!aurigaUid || !sessionToken) {
      setReady(true);
      return;
    }
    fetched.current = true;

    fetchAgents(aurigaUid, sessionToken).then((agents) => {
      if (agents.length > 0) {
        setPath('/agents');
      }
      setReady(true);
    });
  }, [aurigaUid, sessionToken, hydrated, isAppMode]);

  return { path, ready };
}
